import React from 'react';
import { Navigate, Switch } from '@common/components';

import { PrivateRoute } from '@routes/private-route';
import { NoMatch } from '@routes/no-match';

import { SupplierStaffingRequestsPage } from '@applicant-flow/pages/supplier/supplier-staffing-requests-page';
import { StaffingRequestPage } from 'works/pages/supplier/staffing-requests/staffing-request';

import { CandidatesPage } from 'works/pages/supplier/candidates';
import { CandidatePage } from 'works/pages/supplier/candidates/candidate';

import { TeamPage } from '@working-experience/pages/supplier/team-page';

import { BillingPage, ReverseInvoicePage } from '@pay-bill/pages/supplier';

import { AccountSettingsPage } from '@identity/pages/account-settings-page';
import { ReportsPage } from 'works/pages/organization/reports';
import { AssignmentPage } from 'works/pages/organization/team/assignment-page';

import { SupplierCompliancePage } from '@onboarding/pages/supplier/supplier-compliance-page';
import {
  TimesheetsPage,
  WorkerTimesheetPage,
} from '@timekeeping/pages/supplier';
import { UuidRegex } from '@common/services';
import { SupplierAdminSettingsRoutes } from '@routes/supplier/supplier-admin-settings-routes';
import { HomePage } from '@institutions/pages/home-page';

export function SupplierRoutes() {
  return (
    <Switch>
      <PrivateRoute
        path="/works/supplier/admin"
        component={SupplierAdminSettingsRoutes}
        menuItem="admin"
      />
      <PrivateRoute
        exact
        path={`/works/supplier/candidates/:worksProfileId(${UuidRegex})`}
        component={({ match: { params } }) => (
          <Navigate
            to={`/works/supplier/candidates/${params.worksProfileId}/submissions`}
          />
        )}
      />
      <PrivateRoute
        path={`/works/supplier/candidates/:worksProfileId(${UuidRegex})/:tabKey(submissions|profile|reverse_invoices)`}
        component={CandidatePage}
        menuItem="candidates"
      />
      <PrivateRoute
        path="/works/supplier/candidates"
        component={CandidatesPage}
        menuItem="candidates"
      />
      <PrivateRoute
        path="/works/supplier/compliance"
        component={() => <Navigate to="/works/supplier/compliance/expired" />}
        menuItem="compliance"
        exact
      />
      <PrivateRoute
        path="/works/supplier/compliance/:tabKey(expiring|expired)"
        component={SupplierCompliancePage}
        menuItem="compliance"
      />
      <PrivateRoute
        path={`/works/team/:id(${UuidRegex})/:tabKey(assignment|compliance|info|profile|notes)`}
        component={AssignmentPage}
        menuItem="team"
      />
      <PrivateRoute
        exact
        path={`/works/team/:id(${UuidRegex})`}
        component={({ match: { params } }) => (
          <Navigate to={`/works/team/${params.id}/assignment`} />
        )}
      />
      <PrivateRoute path="/works/team" component={TeamPage} menuItem="team" />
      <PrivateRoute
        path={`/works/supplier/staffing_requests/:id(${UuidRegex})/:tab(not_submitted|submitted|offers|accepted|withdrawn|rejected)`}
        component={StaffingRequestPage}
        menuItem="requests"
      />
      <PrivateRoute
        exact
        path={`/works/supplier/staffing_requests/:id(${UuidRegex})`}
        component={({ match: { params } }) => (
          <Navigate
            to={`/works/supplier/staffing_requests/${params.id}/submitted`}
          />
        )}
      />
      <PrivateRoute
        exact
        path="/works/account-settings"
        component={() => (
          <Navigate to="/works/account-settings/user-settings" />
        )}
      />
      <PrivateRoute
        path="/works/account-settings/:page(change-password|notifications|user-settings)"
        component={AccountSettingsPage}
      />
      <PrivateRoute
        path={`/works/supplier/billing/reverse-invoices/:id(${UuidRegex})/:tabKey(summary|hours|disputes|history)`}
        component={ReverseInvoicePage}
        menuItem="billing"
      />
      <PrivateRoute
        exact
        path={`/works/supplier/billing/reverse-invoices/:id(${UuidRegex})`}
        component={({ match: { params } }) => (
          <Navigate
            to={`/works/supplier/billing/reverse-invoices/${params.id}/summary`}
          />
        )}
      />
      <PrivateRoute
        path="/works/supplier/billing/:tab(reverseInvoices|disputes)"
        component={BillingPage}
        menuItem="billing"
      />
      <PrivateRoute
        exact
        path="/works/supplier/billing"
        component={() => (
          <Navigate to="/works/supplier/billing/reverseInvoices" />
        )}
      />
      <PrivateRoute
        exact
        path="/works/supplier/timecards"
        component={() => <Navigate to="/works/supplier/timecards/submitted" />}
      />
      <PrivateRoute
        path="/works/supplier/timecards/:tab(not_submitted|submitted|approved|processing|invoiced|expired|rejected|overdue|all)"
        component={TimesheetsPage}
        menuItem="timecards"
      />
      <PrivateRoute
        path={`/works/supplier/timecard/:id(${UuidRegex})`}
        component={WorkerTimesheetPage}
        menuItem="timecards"
      />
      {/* TODO[WORK-10177]: rename 'index' for 'requests' */}
      <PrivateRoute
        path="/works/supplier/:tab(index|submissions|offers|accepted|extensions)"
        component={SupplierStaffingRequestsPage}
        menuItem="requests"
      />
      <PrivateRoute
        exact
        path="/works/supplier"
        component={HomePage}
        menuItem="home"
      />
      <PrivateRoute
        path="/works/reports"
        component={ReportsPage}
        menuItem="reports"
      />

      {/**
       * [BEGIN] Legacy URLs redirecting
       * */}
      <PrivateRoute
        exact
        path={`/works/supplier/staffing_requests/:id(${UuidRegex})/candidate/:worksProfileId(${UuidRegex})`}
        component={({ match: { params } }) => (
          <Navigate
            to={`/works/supplier/candidates/${params.worksProfileId}/submissions`}
          />
        )}
      />
      {/**
       * [END] Legacy URLs redirecting
       * */}

      <PrivateRoute component={NoMatch} menuItem={null} />
    </Switch>
  );
}
